import React, { useMemo } from "react";
import { Typography } from "../../../common";
import { DataTableContent } from "../../../../types/newtypes";

interface DataTablePreviewProps {
  content: DataTableContent;
  onCompleted?: () => void;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

const DataTablePreview: React.FC<DataTablePreviewProps> = ({
  content,
  onCompleted,
  onCardStateChange,
}) => {
  // Normalize rows to handle both array of arrays and array of objects formats
  const normalizedRows = useMemo(() => {
    if (!content?.rows || content.rows.length === 0) {
      return [];
    }

    // Check if rows are already in array format
    const firstRow = content.rows[0];
    if (Array.isArray(firstRow)) {
      // Already in correct format: array of arrays
      return content.rows as string[][];
    }

    // Convert object format to array format
    // Each row is an object where keys match headers
    if (typeof firstRow === 'object' && content.headers) {
      return content.rows.map((row: any) => {
        // Extract values in the order of headers
        return content.headers.map((header: string) => {
          const value = row[header];
          return value !== undefined ? String(value) : '';
        });
      });
    }

    return [];
  }, [content?.rows, content?.headers]);

  return (
    <div className="w-full mb-4">
      {/* Slide Title */}
      <Typography variant="h4" weight="bold" className="mb-4">
        {content?.slide_title}
      </Typography>

      {/* Intro Text */}
      <Typography variant="p" className="!text-base mb-6">
        {content?.intro_text}
      </Typography>

      {/* Table Title */}
      {content?.title && (
        <Typography variant="h5" weight="bold" className="mb-4">
          {content.title}
        </Typography>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Table Header */}
            <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <tr>
                {content?.headers?.map((header, index) => (
                  <th
                    key={index}
                    className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b border-gray-200"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-gray-100">
              {normalizedRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="px-6 py-4 text-sm text-gray-900"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DataTablePreview;
